// @ts-check
import {defineStore} from "pinia";
import {
    rateContentItem,
    PageData
} from "../services/content";
import {bookmark, voteForFighter} from "../services/user";
import {computed, ref, watch} from "vue";
import {useContentStore} from "./content";
import {hasCookie} from "../../../lib/utils/cookies";

export const useStore = defineStore("store", () => {
    const contentItem = useContentStore()
    const page = ref("details")
    const itemLoaded = ref(false)
    const itemVersion = ref("")
    const watchedPreview = ref(false)
    const startingPosition = ref(0)
    const backdrop = ref(false)
    const modal = ref('')
    const chatOpened = ref(true)
    const castActive = ref(false)

    const itemId = computed(() => {
        return contentItem.itemId
    })

    const description = computed(() => {
        return contentItem.description
    })

    function detailsScreen() {
        page.value = "details"
    }

    function toggleBackdrop(visible?: boolean){
        if (typeof visible !== 'undefined') {
            backdrop.value = visible
        }else{
            backdrop.value = !backdrop.value
        }
    }

    function toggleModal(name?: string){
        if (typeof name === 'undefined') {
            modal.value = ''
        }else if(name === 'rate' && !hasCookie('eid', document.cookie)){
            modal.value = 'sign-in'
        }else{
            modal.value = String(name)
        }
        toggleBackdrop()
    }

    async function playerScreen(position: number){
        // console.log("requested player screen at position", position)
        return contentItem.fetchStream("local")
            .then(() => {
                startingPosition.value = position
                page.value = "player"
            })
    }

    function updateCurrentPosition(position: number){
        startingPosition.value = position
    }

    function previewScreen(){
        page.value = "preview"
    }

    function previewEnded(){
        watchedPreview.value = true
        detailsScreen()
    }

    function setInitialItemData(data: PageData): void {
        contentItem.initialData(data)
    }

    function updateDataLayer(){
        if (contentItem.billing?.is_chargeable && contentItem.billing?.price){
            window.dataLayer?.push({
                ecomm_pagetype: "product",
                ecomm_prodid: contentItem.itemId,
                ecomm_totalvalue: contentItem.billing.price.amount,
                price: contentItem.billing.price.amount,
                currency: contentItem.billing.price.currency,
                package_id: contentItem.billing.package_id,
                package_name: contentItem.billing.package_name,
                product_type: contentItem.billing.type,
            })
            window.dataLayer?.push({event: 'embed_load'})
        }
    }

    async function fetchContentItem(itemId: string | null) {
        // console.log("fetching item:", itemId)
        if (!itemId)
            return false

        itemLoaded.value = false

        itemLoaded.value = await contentItem.fetchItem(itemId)

        updateItemVersion()
        updateDataLayer()
        return contentItem
    }

    async function switchItem(itemId: string | null) {
        // console.log("switching to item:", itemId)
        if (!itemId)
            return false

        try{
            await fetchContentItem(itemId)

            if (page.value === "player") {
                if (contentItem.billing?.is_chargeable && !contentItem.billing?.is_purchased){
                    detailsScreen()
                }else{
                    await contentItem.fetchStream("local")
                }
            }

            let alternative = null;
            try{
                const data = localStorage.getItem('fite.alt');
                if (data){
                    alternative = JSON.parse(data);
                }
                // eslint-disable-next-line no-empty
            }catch (e){}
            if (!alternative){
                alternative = {data: {}}
            }
            const langCode = contentItem.languages.filter((row) => row.selected).pop()?.code
            if (langCode){
                alternative.data[contentItem.mainItemId] = {lang: langCode, ts: Math.round(new Date().getTime()/1000)};
                window.postMessage({e: "language-switch", lang: langCode})
            }
            localStorage.setItem('fite.alt', JSON.stringify(alternative))
        }catch (err){
            console.log(err)
            return false
        }
        return true
    }

    function updateItemVersion(){
        itemVersion.value = new Date().toISOString()
        contentItem.reCalculateTags()
    }

    function rateItem(score: number){
        return rateContentItem(itemId.value, score)
            .then((response) => {
                if (window.dataLayer){
                    window.dataLayer.push({event: "rate"})
                }
                return response
            })
    }

    function vote(matchId: number, fighterId: number){
        let votes: {[key: number]: number} | null = null;
        try {
            const data = localStorage.getItem('tsd.vote');
            if (data){
                votes = JSON.parse(data)
            }
        }catch (e){
            //e
        }
        return voteForFighter(matchId, fighterId)
            .then((response) => {
                if (window.dataLayer){
                    window.dataLayer.push({'event': 'vote'});
                }
                if (!votes){
                    votes = {}
                }
                votes[matchId] = fighterId
                try{
                    localStorage.setItem('tsd.vote', JSON.stringify(votes))
                }catch (e){
                    //e
                }
                return response
            })
    }

    function isBookmarked(){
        return contentItem.tags.includes("bookmarked")
    }

    function bookmarkItem(itemId: string, isNew: boolean){
        return bookmark(itemId, isNew).then((result) => {
            if (isNew){
                contentItem.addTag("bookmarked")
                if (window.dataLayer){
                    window.dataLayer.push({event: "bookmark"})
                }
            }else{
                contentItem.removeTag("bookmarked")
            }
            return result
        })
    }

    function toggleChat(){
        chatOpened.value = !chatOpened.value;
    }

    function setCastActive(){
        castActive.value = true
        startingPosition.value = 0
        page.value = "player"
    }

    watch(() => backdrop.value, (value) => {
        const el = document.querySelector<HTMLElement>("body");
        if (!el)
            return

        if (value){
            el.classList.add("overflow-hidden")
        }else{
            el.classList.remove("overflow-hidden")
        }

    })

    return {
        page,
        itemId,
        description,
        itemLoaded,
        itemVersion,
        startingPosition,
        watchedPreview,
        modal,
        chatOpened,
        castActive,
        detailsScreen,
        playerScreen,
        previewScreen,
        fetchContentItem,
        switchItem,
        setInitialItemData,
        updateItemVersion,
        toggleBackdrop,
        previewEnded,
        toggleModal,
        rateItem,
        bookmarkItem,
        isBookmarked,
        vote,
        toggleChat,
        updateCurrentPosition,
        setCastActive
    }
})
